import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Upload, MapPin, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExaminationCenterApplyPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    center_name: '',
    center_type: '',
    registration_number: '',
    country: '',
    city: '',
    address: '',
    contact_person: '',
    designation: '',
    email: '',
    phone: '',
    facility_description: '',
    osce_stations_capacity: '',
    written_exam_capacity: '',
    equipment_available: '',
    previous_exam_experience: '',
    why_examination_center: '',
    declaration: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.declaration) { toast.error('You must accept the declaration'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/applications/examination-center`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      toast.success('Application submitted successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="exam-center-apply-success">
        <Card className="imcac-card max-w-lg w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">Application Submitted</h2>
            <p className="text-sm text-slate-600 mb-6">Your examination center application has been received and will be reviewed by the Accreditation Committee. You will be contacted for site inspection scheduling.</p>
            <Button onClick={() => navigate('/dashboard/examination-center')} className="bg-[#1B2A4A] text-white rounded-sm">Go to Examination Center Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="exam-center-apply-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Institutional Accreditation</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Examination Center Application</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            For institutions seeking accreditation to host IMCAC competency-based examinations including written exams and OSCE assessments.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-slate-600 hover:text-[#1B2A4A]">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>

          <Card className="imcac-card">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
                <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-[#C5A572]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#1B2A4A] font-heading">Examination Center Application</h2>
                  <p className="text-xs text-slate-500">Apply to become an accredited IMCAC examination venue</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Center Details */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Center Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Center/Facility Name *</Label>
                      <Input value={form.center_name} onChange={e => setForm({...form, center_name: e.target.value})} required className="rounded-sm mt-1" placeholder="XYZ Medical Training Center" data-testid="center-name" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Center Type *</Label>
                      <Select value={form.center_type} onValueChange={v => setForm({...form, center_type: v})}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="center-type"><SelectValue placeholder="Select center type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Hospital">Hospital</SelectItem>
                          <SelectItem value="Medical College">Medical College</SelectItem>
                          <SelectItem value="Training Academy">Training Academy</SelectItem>
                          <SelectItem value="Simulation Center">Simulation Center</SelectItem>
                          <SelectItem value="Conference Facility">Conference Facility</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Registration Number</Label>
                      <Input value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} className="rounded-sm mt-1" placeholder="Facility registration number (if applicable)" data-testid="center-reg" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Country *</Label>
                      <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} required className="rounded-sm mt-1" placeholder="India" data-testid="center-country" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-slate-700">City *</Label>
                      <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} required className="rounded-sm mt-1" placeholder="Mumbai" data-testid="center-city" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-sm font-medium text-slate-700">Full Address *</Label>
                    <Textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} required rows={2} className="rounded-sm mt-1 resize-none" placeholder="Complete address including landmark, city, state, and postal code" data-testid="center-address" />
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Contact Person *</Label>
                      <Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} required className="rounded-sm mt-1" placeholder="Dr. Jane Doe" data-testid="center-contact" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Designation *</Label>
                      <Input value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} required className="rounded-sm mt-1" placeholder="Director / Administrator" data-testid="center-designation" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="rounded-sm mt-1" placeholder="contact@center.com" data-testid="center-email" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Phone *</Label>
                      <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required className="rounded-sm mt-1" placeholder="+91 98765 43210" data-testid="center-phone" />
                    </div>
                  </div>
                </div>

                {/* Facility Details */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Facility & Capacity Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Facility Description *</Label>
                      <Textarea 
                        value={form.facility_description} 
                        onChange={e => setForm({...form, facility_description: e.target.value})} 
                        required 
                        rows={3} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="Describe your facility including total area, number of rooms, clinical setup areas, waiting areas, and general infrastructure..."
                        data-testid="center-facility" 
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">OSCE Stations Capacity *</Label>
                        <Input type="number" value={form.osce_stations_capacity} onChange={e => setForm({...form, osce_stations_capacity: e.target.value})} required className="rounded-sm mt-1" placeholder="Number of OSCE stations possible (e.g., 10)" data-testid="center-osce" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Written Exam Capacity *</Label>
                        <Input type="number" value={form.written_exam_capacity} onChange={e => setForm({...form, written_exam_capacity: e.target.value})} required className="rounded-sm mt-1" placeholder="Number of candidates (e.g., 50)" data-testid="center-written" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Equipment & Resources Available *</Label>
                      <Textarea 
                        value={form.equipment_available} 
                        onChange={e => setForm({...form, equipment_available: e.target.value})} 
                        required 
                        rows={3} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="List available equipment: mannequins, simulation models, injection trainers, laser equipment (if any), computers for CBT, AV equipment, etc."
                        data-testid="center-equipment" 
                      />
                    </div>
                  </div>
                </div>

                {/* Experience & Motivation */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Experience & Motivation</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Previous Examination Hosting Experience</Label>
                      <Textarea 
                        value={form.previous_exam_experience} 
                        onChange={e => setForm({...form, previous_exam_experience: e.target.value})} 
                        rows={3} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="Describe any previous experience hosting professional examinations, certifications, or assessments (if applicable)..."
                        data-testid="center-experience" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Why Become an IMCAC Examination Center? *</Label>
                      <Textarea 
                        value={form.why_examination_center} 
                        onChange={e => setForm({...form, why_examination_center: e.target.value})} 
                        required 
                        rows={4} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="Explain your motivation for becoming an accredited examination center, how it aligns with your institutional goals, and what value you can provide to candidates..."
                        data-testid="center-why" 
                      />
                    </div>
                  </div>
                </div>

                {/* Document Upload Notice */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
                  <p className="text-sm font-medium text-slate-700 mb-2">Site Inspection & Documentation</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Upload className="h-3.5 w-3.5" /> 
                    <span>Facility photographs, floor plans, and equipment inventory will be requested upon preliminary approval. A site inspection will be scheduled before final accreditation.</span>
                  </div>
                </div>

                {/* Declaration */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <Checkbox checked={form.declaration} onCheckedChange={v => setForm({...form, declaration: v})} className="mt-1" data-testid="center-declaration" />
                  <label className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I declare that all information provided is accurate and complete. I understand that accreditation as an examination center requires compliance with IMCAC examination standards, periodic audits, and maintenance of quality infrastructure. I agree to cooperate with site inspections and adhere to examination conduct guidelines.
                  </label>
                </div>

                <Button type="submit" disabled={loading || !form.declaration} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm py-3" data-testid="center-submit">
                  {loading ? 'Submitting Application...' : 'Submit Examination Center Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
